import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Custom Turbo Stream action — registered once at module level
if (!Turbo.StreamActions.renderComplete) {
  Turbo.StreamActions.renderComplete = function () {
    document.dispatchEvent(new CustomEvent("telemetry:render-complete", {
      detail: {
        renderId: this.getAttribute("data-render-id"),
        status:   this.getAttribute("data-status")
      }
    }))
  }
}

export default class extends Controller {
  static values  = { renderId: Number }
  static targets = ["statusBadge", "completedCount", "totalCount",
                    "stepsBody", "mediaBody"]

  connect() {
    this._onRenderComplete = this.handleRenderComplete.bind(this)
    this._onStreamRender   = this.updateCounts.bind(this)

    document.addEventListener("telemetry:render-complete", this._onRenderComplete)
    document.addEventListener("turbo:before-stream-render", this._onStreamRender)
  }

  disconnect() {
    document.removeEventListener("telemetry:render-complete", this._onRenderComplete)
    document.removeEventListener("turbo:before-stream-render", this._onStreamRender)
  }

  handleRenderComplete(event) {
    const { renderId, status } = event.detail
    if (Number(renderId) !== this.renderIdValue) return

    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.className = `telemetry__badge telemetry__badge--${status}`
      this.statusBadgeTarget.textContent = status
    }

    if (status === "completed") this.loadOutputPanel()
  }

  updateCounts() {
    if (!this.hasCompletedCountTarget || !this.hasStepsBodyTarget) return
    // Defer until Turbo finishes applying the stream
    requestAnimationFrame(() => {
      const completed = this.stepsBodyTarget.querySelectorAll(".telemetry__badge--completed").length
      const rows      = this.stepsBodyTarget.querySelectorAll("tr")
      this.completedCountTarget.textContent = completed
      if (this.hasTotalCountTarget) this.totalCountTarget.textContent = rows.length
    })
  }

  openPreview(event) {
    const { previewUrl, previewType } = event.currentTarget.dataset
    if (!previewUrl || !previewType) return
    window.dispatchEvent(new CustomEvent("preview-modal:open", {
      detail: { url: previewUrl, type: previewType }
    }))
  }

  async loadOutputPanel() {
    try {
      const resp = await fetch(`/telemetry/${this.renderIdValue}/output_panel`)
      if (!resp.ok) return

      const html = await resp.text()
      if (!this.hasMediaBodyTarget || !html.trim().length) return

      // Insert or replace output pane
      const existingPane = this.mediaBodyTarget.querySelector('[data-tab="output"]')
      if (existingPane) {
        existingPane.outerHTML = html
      } else {
        const tmp  = document.createElement("template")
        tmp.innerHTML = html.trim()
        const pane = tmp.content.firstElementChild
        pane.hidden = true
        this.mediaBodyTarget.prepend(pane)
      }

      // Insert tab button if missing
      const tabList = this.element.querySelector('[role="tablist"]')
      if (tabList && !tabList.querySelector('[data-tab="output"]')) {
        tabList.insertAdjacentHTML("afterbegin", `
          <button type="button" role="tab" class="telemetry__media-tab"
                  data-telemetry-tabs-target="tab"
                  data-action="click->telemetry-tabs#select"
                  data-tab="output" aria-selected="false">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="2"/>
              <polygon points="10 8 16 12 10 16 10 8"/>
            </svg>
            Output
          </button>
        `)
      }

      // Activate via telemetry-tabs controller
      this.element.querySelector('[role="tablist"] [data-tab="output"]')?.click()
    } catch (_) {}
  }
}
